/**
 * THE OPENING HOURS AS ONE FILL VALUE — owner request 2026-10-18 ("change the timezones here to be 10-18" on New agent →
 * Content; design §31.9.3 amended 2026-10-18; ADR 0065 amendment of the same date).
 *
 * The opening-hours editor (the wizard's week, the agent page's "When it answers" card) holds a week of shifts, the
 * VoiceAgent's own time zone and its special dates (ADR 0044). The help assistant proposes ONE change set for it from
 * natural language ("open Mon–Fri 10:00–18:00, closed weekends", «Δευτέρα–Παρασκευή 10–18»), as one value of the
 * fillable kind `opening_hours`:
 *
 * ```
 * {
 *   "time_zone":    "Europe/Athens",                                   // optional: the zone the times are in
 *   "day":          { "1": ["10:00-18:00"], "6": [], "7": [] },        // optional: ISO weekday → 0–3 shifts; [] = closed
 *   "special_date": [{ "date": "2026-12-25", "label": "", "interval": [] }]   // optional: added, or replacing that date
 * }
 * ```
 *
 * WHAT A CHANGE SET DOES (`mergeOpeningHoursFill`): the days it names replace those days' shifts; the zone, when named,
 * replaces the zone; each special date is added, or replaces the entry of the same date — never removes one (a removal
 * stays the person's own chip button). Days and dates it does not name are left exactly as they are. The page reports
 * the editor's whole state in the same shape (every day, the zone, every special date), so the model can see what is
 * there, and an undo puts that whole state back.
 *
 * A shift is `HH:MM-HH:MM`, 24-hour, as the editor's time boxes hold it; `00:00-24:00` is the whole day (the editor's
 * "All day"; `00:00-00:00` is read as it), alone in its day; a shift that opens and closes at the same time is refused,
 * as the editor refuses it; a closing time earlier than the opening runs past midnight, as the build reads it. A
 * weekday may be named by its whole English or Greek name instead of its number (EVAL-RUN rounds 1–2 found the model
 * writing names for ISO numbers); the value is always written back with numbers.
 *
 * Imports nothing: the protocol guards, the widget's panel chunk and the help host all read it.
 */
/** At most this many shifts a day (the wizard's week editor hides "Add hours" at three). */
export const OPENING_HOURS_SHIFT_LIMIT = 3;
/** At most this many special dates (the API's `special_date` `maxItems`, IC-DG-2). */
export const OPENING_HOURS_SPECIAL_DATE_LIMIT = 60;
/** A special date's name ("Christmas") is at most this many characters. */
export const OPENING_HOURS_LABEL_CHAR_LIMIT = 60;
/** The whole day, as the editor writes it. */
export const OPENING_HOURS_WHOLE_DAY_SHIFT = "00:00-24:00";
/** An ISO weekday as a key: "1" Monday … "7" Sunday. */
export const ISO_DAY_CODE = ["1", "2", "3", "4", "5", "6", "7"];
const refused = (reason) => ({ ok: false, reason });
function recordOf(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : null;
}
/** C0 and C1 controls, line and paragraph separators. */
// eslint-disable-next-line no-control-regex -- the point of the pattern is to find control characters
const CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;
const SHIFT = /^\s*(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})\s*$/u;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** An IANA zone name's shape (`Europe/Athens`, `America/Argentina/Buenos_Aires`, `UTC`); the page checks its own list. */
const TIME_ZONE = /^(?:UTC|Etc\/UTC|[A-Z][A-Za-z_-]{1,30}(?:\/[A-Za-z0-9_+-]{1,30}){1,2})$/u;
/**
 * A weekday's full name in English or Greek, any case, with or without accents → its ISO number. Only whole names,
 * never an abbreviation, so each maps to exactly one day.
 */
const WEEKDAY_NAME = Object.freeze({
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
    δευτερα: 1,
    τριτη: 2,
    τεταρτη: 3,
    πεμπτη: 4,
    παρασκευη: 5,
    σαββατο: 6,
    κυριακη: 7,
});
/** The ISO number of a weekday's whole English or Greek name, or `undefined`. */
export function isoWeekdayOfName(name) {
    const key = name
        .trim()
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .toLowerCase();
    return Object.hasOwn(WEEKDAY_NAME, key) ? WEEKDAY_NAME[key] : undefined;
}
function dayCodeOf(key) {
    if (ISO_DAY_CODE.includes(key))
        return key;
    const named = isoWeekdayOfName(key);
    return named === undefined ? null : String(named);
}
function two(value) {
    return String(value).padStart(2, "0");
}
/** One shift as `HH:MM-HH:MM`, or why not. `00:00-00:00` reads as the whole day. */
function parseShift(raw) {
    if (typeof raw !== "string")
        return refused("wrong_type");
    const match = SHIFT.exec(raw);
    if (match === null)
        return refused("not_a_time");
    const [opensHour, opensMinute, closesHour, closesMinute] = [match[1], match[2], match[3], match[4]].map(Number);
    if (opensHour > 23 || opensMinute > 59 || closesMinute > 59)
        return refused("not_a_time");
    const opens = `${two(opensHour)}:${two(opensMinute)}`;
    if (closesHour === 24 && closesMinute === 0) {
        return opens === "00:00" ? { ok: true, value: OPENING_HOURS_WHOLE_DAY_SHIFT } : refused("not_a_time");
    }
    if (closesHour > 23)
        return refused("not_a_time");
    const closes = `${two(closesHour)}:${two(closesMinute)}`;
    if (opens === closes)
        return opens === "00:00" ? { ok: true, value: OPENING_HOURS_WHOLE_DAY_SHIFT } : refused("out_of_range");
    return { ok: true, value: `${opens}-${closes}` };
}
/** A day's shifts: 0–3, distinct, the whole day alone. */
function parseShiftList(raw) {
    if (!Array.isArray(raw))
        return refused("wrong_type");
    if (raw.length > OPENING_HOURS_SHIFT_LIMIT)
        return refused("too_long");
    const value = [];
    for (const one of raw) {
        const shift = parseShift(one);
        if (!shift.ok)
            return shift;
        if (value.includes(shift.value))
            return refused("repeated");
        value.push(shift.value);
    }
    if (value.includes(OPENING_HOURS_WHOLE_DAY_SHIFT) && value.length > 1)
        return refused("out_of_range");
    return { ok: true, value: Object.freeze(value) };
}
function isCalendarDate(raw) {
    if (typeof raw !== "string")
        return false;
    const match = ISO_DATE.exec(raw);
    if (match === null)
        return false;
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const at = new Date(Date.UTC(year, month - 1, day));
    return at.getUTCFullYear() === year && at.getUTCMonth() === month - 1 && at.getUTCDate() === day;
}
function parseSpecialDate(raw) {
    const entry = recordOf(raw);
    if (entry === null)
        return refused("wrong_type");
    for (const key of Object.keys(entry))
        if (key !== "date" && key !== "label" && key !== "interval")
            return refused("wrong_type");
    if (!isCalendarDate(entry["date"]))
        return refused("not_a_date");
    const rawLabel = entry["label"] ?? "";
    if (typeof rawLabel !== "string")
        return refused("wrong_type");
    const label = rawLabel.trim();
    if (CONTROL.test(label))
        return refused("control_character");
    if (Array.from(label).length > OPENING_HOURS_LABEL_CHAR_LIMIT)
        return refused("too_long");
    const interval = parseShiftList(entry["interval"] ?? []);
    if (!interval.ok)
        return interval;
    return { ok: true, value: Object.freeze({ date: entry["date"], label, interval: interval.value }) };
}
/**
 * A raw value as an opening-hours change set (or whole state), or why not. Total over `unknown`; never throws. Keys
 * outside the three are refused; at least one must be present; the value is written back canonically (days in ISO
 * order, special dates in date order, every special date with its label and its shifts).
 */
export function parseOpeningHoursFill(raw) {
    const value = recordOf(raw);
    if (value === null)
        return refused("wrong_type");
    const keys = Object.keys(value);
    if (keys.length === 0)
        return refused("empty");
    for (const key of keys)
        if (key !== "time_zone" && key !== "day" && key !== "special_date")
            return refused("wrong_type");
    const out = {};
    if ("time_zone" in value) {
        const zone = value["time_zone"];
        if (typeof zone !== "string")
            return refused("wrong_type");
        if (!TIME_ZONE.test(zone))
            return refused("not_a_time_zone");
        out.time_zone = zone;
    }
    if ("day" in value) {
        const day = recordOf(value["day"]);
        if (day === null)
            return refused("wrong_type");
        const dayKeys = Object.keys(day);
        if (dayKeys.length === 0)
            return refused("empty");
        const read = {};
        for (const key of dayKeys) {
            const code = dayCodeOf(key);
            if (code === null)
                return refused("out_of_range");
            if (Object.hasOwn(read, code))
                return refused("repeated");
            const shift = parseShiftList(day[key]);
            if (!shift.ok)
                return shift;
            read[code] = shift.value;
        }
        const sorted = {};
        for (const code of ISO_DAY_CODE)
            if (read[code] !== undefined)
                sorted[code] = read[code];
        out.day = Object.freeze(sorted);
    }
    if ("special_date" in value) {
        const given = value["special_date"];
        if (!Array.isArray(given))
            return refused("wrong_type");
        if (given.length > OPENING_HOURS_SPECIAL_DATE_LIMIT)
            return refused("too_long");
        const entries = [];
        for (const one of given) {
            const entry = parseSpecialDate(one);
            if (!entry.ok)
                return entry;
            if (entries.some((each) => each.date === entry.value.date))
                return refused("repeated");
            entries.push(entry.value);
        }
        out.special_date = Object.freeze(entries.sort((left, right) => left.date.localeCompare(right.date)));
    }
    return { ok: true, value: Object.freeze(out) };
}
/**
 * The editor's state after a change set: the named days replaced, the zone replaced when named, each special date added
 * or replacing its date (never removed), everything else kept. `before` is the editor's whole state (`null`: none read).
 */
export function mergeOpeningHoursFill(before, change) {
    const out = {};
    const zone = change.time_zone ?? before?.time_zone;
    if (zone !== undefined)
        out.time_zone = zone;
    if (before?.day !== undefined || change.day !== undefined) {
        const day = {};
        for (const code of ISO_DAY_CODE) {
            const shift = change.day?.[code] ?? before?.day?.[code];
            if (shift !== undefined)
                day[code] = shift;
        }
        out.day = Object.freeze(day);
    }
    if (before?.special_date !== undefined || change.special_date !== undefined) {
        const named = new Set((change.special_date ?? []).map((one) => one.date));
        out.special_date = Object.freeze([...(before?.special_date ?? []).filter((one) => !named.has(one.date)), ...(change.special_date ?? [])].sort((left, right) => left.date.localeCompare(right.date)));
    }
    return Object.freeze(out);
}
/** A canonical text of a value: two values say the same thing exactly when their keys are equal. */
export function openingHoursKeyOf(value) {
    const day = ISO_DAY_CODE.map((code) => `${code}=${value.day?.[code]?.join(",") ?? "~"}`).join(";");
    const date = (value.special_date ?? []).map((one) => `${one.date}|${one.label}|${one.interval.join(",")}`).join(";");
    return `${value.time_zone ?? "~"}#${value.day === undefined ? "~" : day}#${value.special_date === undefined ? "~" : date}`;
}
//# sourceMappingURL=opening-hours-fill.js.map